/* eslint-disable jsx-a11y/no-noninteractive-tabindex */
import React, { PropsWithChildren, useEffect, useRef } from 'react';
import { HidingStrategy, useTabBarContext } from '../Context';
import { sendLiveRegionMessage } from '../../../atoms/LiveRegionGlobal/LiveRegionGlobal';
import { LoadingSpinner } from '../../../atoms/LoadingSpinner';
import { RefetchBlock } from '../../RefetchBlock';
import { KeyEvent } from '../../../../types/KeyEvent';

type TabPanelProps = PropsWithChildren<{
  id: string;
  buttonLabel: string;
  disabled?: boolean;
}>;

export default function TabPanel({ id, children, buttonLabel, disabled }: TabPanelProps) {
  const {
    registerOrUpdateTabItem,
    unregisterTabItem,
    isActiveItem,
    focusById,
    hidingStrategy,
    setActiveTab,
    fetchingData,
    footerButtonsClicked,
    setFooterButtonsClicked,
  } = useTabBarContext();

  const isActive = isActiveItem(id);

  const tabPanelRef = useRef<HTMLDivElement>(null);
  /**
   * onKeyDown is triggered here for Shift + Tab
   * because we want to make sure, that a Shift + Tab from the tab panel
   * should lead to the current corresponding active tab
   * @param event
   */
  const onKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    const panelId = `${id}-tabpanel`;
    const focusedElementId = document.activeElement?.id;

    if (focusedElementId === panelId && event.shiftKey && event.code === KeyEvent.Tab) {
      event.preventDefault();
      focusById(`${id}-tab`);
    }
  };

  useEffect(() => {
    registerOrUpdateTabItem({ id, buttonLabel, disabled: disabled || false });
    // registerOrUpdateTabItem results in infinite loop
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [disabled, id, buttonLabel]);

  useEffect(() => {
    return () => {
      unregisterTabItem(id);
    };
    // we only want this to happen on unmount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /**
   * After the data is reloaded, whole components stuff is re-rendered. Browser
   * does not know from previous state and sets focus again to initial state.
   * That's why we pass via callback a function that sets focus to correct
   * position after successful reloading of data.
   * */
  useEffect(() => {
    if (isActive) {
      fetchingData?.fetchFormParams(() => {
        if (footerButtonsClicked) {
          focusById(`${id}-tabpanel`);
          setFooterButtonsClicked(false);
        }
      });
      // This line is responsible for setting focus on LoadingSpinner and Fetching Error Block
      if (fetchingData?.isLoading || fetchingData?.requestError) {
        focusById(`${id}-tabpanel`);
      }
    }
    // Adding fetchingData results in a request being fired when a radio button
    // is clicked!!
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isActive]);

  const getTabContent = () => {
    if (fetchingData && fetchingData.tabIndex !== 0 && isActive) {
      if (fetchingData.isLoading) {
        // initial loading spinner
        sendLiveRegionMessage(fetchingData.loadingMessage);
        return (
          <div className="tabs__tab-button">
            <div className="tabs__loading-spinner-container">
              <div className="tabs__loading-spinner">
                <LoadingSpinner />
              </div>
            </div>
          </div>
        );
      }
      if (fetchingData.requestError) {
        return (
          <div className="tabs__tab-button">
            <RefetchBlock
              // As above we must pass here also for refetchBlock function set focus
              refetchBlock={() =>
                fetchingData.fetchFormParams(() => {
                  focusById(`${id}-tabpanel`);
                  setFooterButtonsClicked(false);
                })}
            />
          </div>
        );
      }
    }

    if (children && !disabled && hidingStrategy === HidingStrategy.CSS_ONLY) {
      return <div className={isActive ? 'tabpanel__content-container' : 'invisible'}>{children}</div>;
    }

    if (isActive) {
      return children;
    }
  };

  return (
    // keyDown handler is ok here, because we have tabindex="0" and a name for the panel
    // eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions
    <div
      tabIndex={isActive ? 0 : -1}
      className="tabpanel"
      ref={tabPanelRef}
      role="tabpanel"
      id={`${id}-tabpanel`}
      aria-labelledby={`${id}-tab`}
      aria-hidden={isActive ? 'false' : 'true'}
      onFocus={() => !isActive && setActiveTab(id)} // this makes sure we jump in this panel, when the focus is inside
      onKeyDown={isActive ? event => onKeyDown(event) : undefined} // only on active panels
    >
      {getTabContent()}
    </div>
  );
}
